import Grid from "@material-ui/core/Grid";
import {Card, CardContent} from "@mui/material";
import React from "react";
import {useTranslation} from "react-i18next";
import {ActivityCard} from "../../common/card/Cards";
import SectionHeader from "../../common/card/SectionHeader";
import {useGlobalState} from "../../GlobalContext";
import {GasStationActivityType, useGasStationActivity} from "../../hooks/useGasStationActivity";

const ActivityWidget = ({
    // Query params
    selectedGSZIds = [],
    dateRange = {},
    
    // For backward compatibility
    cards = null
}) => {
    const { t } = useTranslation();
    const { state } = useGlobalState();
    
    // Fetch activity data using our custom hook
    const { 
        data: activityData,
        isLoading,
        error
    } = useGasStationActivity({
        market: state.country,
        selectedGSZIds,
        dateRange,
        enabled: selectedGSZIds.length > 0
    });
    
    // Prepare cards data based on API response
    const getCardData = () => {
        if (cards) {
            // If cards are provided as props, use them (for backward compatibility)
            return cards;
        }
        
        if (!activityData || !activityData.gasStationActivities) {
            return [
                {
                    title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.ACTIVE_IN_PERIOD),
                    value: "-",
                    percentage: "-",
                    color: "green",
                },
                {
                    title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.INACTIVE_IN_PERIOD),
                    value: "-",
                    percentage: "-",
                    color: "yellow",
                },
                {
                    title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.NEVER_ACTIVE),
                    value: "-",
                    percentage: "-",
                    color: "red",
                },
                {
                    title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.TOTAL),
                    value: "-",
                    percentage: "-",
                    color: "gray",
                }
            ];
        }
        
        const activities = activityData.gasStationActivities;
        const total = activities[GasStationActivityType.TOTAL] || 0;
        const inactiveWithoutNeverActive = activities[GasStationActivityType.INACTIVE_IN_PERIOD] - activities[GasStationActivityType.NEVER_ACTIVE];
        
        return [
            {
                title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.ACTIVE_IN_PERIOD),
                value: activities[GasStationActivityType.ACTIVE_IN_PERIOD]?.toString() || "0",
                percentage: total > 0 
                    ? Math.round((activities[GasStationActivityType.ACTIVE_IN_PERIOD] / total) * 100).toString()
                    : "0",
                color: "green",
            },
            {
                title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.INACTIVE_IN_PERIOD),
                value: inactiveWithoutNeverActive?.toString() || "0",
                percentage: total > 0 
                    ? Math.round((inactiveWithoutNeverActive/ total) * 100).toString()
                    : "0",
                color: "yellow",
            },
            {
                title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.NEVER_ACTIVE),
                value: activities[GasStationActivityType.NEVER_ACTIVE]?.toString() || "0",
                percentage: total > 0 
                    ? Math.round((activities[GasStationActivityType.NEVER_ACTIVE] / total) * 100).toString()
                    : "0",
                color: "red",
            },
            {
                title: t("Reports.Dashboard.ActivityType." + GasStationActivityType.TOTAL),
                value: activities[GasStationActivityType.TOTAL]?.toString() || "0",
                percentage: total > 0 ? 100 : 0,
                color: "gray",
            }
        ];
    };
    
    const displayCards = getCardData();

    // Show error state if API call failed
    if (error) {
        console.error("Error fetching gas station activity data:", error);
    }

    return (
        <Card>
            <CardContent>
                <SectionHeader
                    title={t("Reports.Dashboard.GSActivity")}
                    subtitle={t("Reports.Dashboard.InThisPeriod")}
                    description={t("Reports.Dashboard.Percentages")}
                />
                {/* Activity Status Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }} className={"tailwind-scope"}>
                    {displayCards.map((card, index) => (
                        <Grid 
                            key={index} 
                            item 
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <ActivityCard
                                title={card.title}
                                value={isLoading ? "-" : card.value}
                                percentage={isLoading ? "" : (card.percentage ? card.percentage + "%" : "")}
                                color={card.color}
                            />
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default ActivityWidget;